import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flag, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuggestCorrection({ gkey, placeName, sections }) {
  const { tr } = useI18n();
  const { user, signIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState(sections[0] || "other");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/corrections`, { key: gkey, section, message: message.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem("sp_token")}` } });
      toast.success(tr("Thank you — your correction is queued for editorial review."));
      setMessage("");
      setOpen(false);
    } catch {
      toast.error(tr("Could not submit — please sign in again."));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="suggest-correction-button"
          className="inline-flex items-center gap-2 text-xs font-extrabold text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] rounded-full px-4 py-2 hover:bg-[#FEF3C7] transition-colors">
          <Flag size={12} /> {tr("Suggest a correction")}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md" data-testid="correction-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2557]">{tr("Suggest a correction")} — {placeName}</DialogTitle>
        </DialogHeader>
        {user ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-extrabold text-[#475569] uppercase tracking-wider">{tr("Section")}</label>
              <select data-testid="correction-section-select" value={section} onChange={(e) => setSection(e.target.value)}
                className="w-full mt-1 text-sm px-3 py-2.5 rounded-xl border border-[#E2E8F0] bg-white text-[#0F2557] capitalize focus:outline-none focus:border-[#B45309]">
                {sections.map((s) => <option key={s} value={s}>{tr(s.charAt(0).toUpperCase() + s.slice(1))}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-extrabold text-[#475569] uppercase tracking-wider">{tr("What should be fixed?")}</label>
              <textarea data-testid="correction-message-input" value={message} onChange={(e) => setMessage(e.target.value)}
                maxLength={3000} rows={4}
                placeholder={tr("Describe the inaccuracy and, if possible, the correct information and a source…")}
                className="w-full mt-1 text-sm px-3 py-2.5 rounded-xl border border-[#E2E8F0] text-[#0F2557] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#B45309]" />
            </div>
            <button data-testid="correction-submit-button" onClick={submit} disabled={sending || !message.trim()}
              className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-40 text-white font-bold text-sm py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
              {sending && <Loader2 size={14} className="animate-spin" />} {tr("Submit for review")}
            </button>
            <p className="text-[0.6rem] text-[#94A3B8]">{tr("Corrections are reviewed by editors before any content changes. Thank you for keeping WorldPulse accurate.")}</p>
          </div>
        ) : (
          <button data-testid="correction-signin-button" onClick={signIn}
            className="w-full text-sm font-bold text-[#B45309] bg-[#FFFBEB] hover:bg-[#FEF3C7] rounded-xl py-3 transition-colors">
            {tr("Sign in with Google to suggest corrections")}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
